import { Anchor, Checkbox, Divider, Group, Stack, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { IconFigmaArrowRight, IconFigmaBuilding } from '@/assets/icons';
import {
  FormErrorMessage,
  MutationButton,
  PasswordField,
  SecondaryEntryCard,
  TextField
} from '@/components/common';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { brandTextStyle } from '@/features/auth/components/authFieldStyles';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authErrorMessage } from '@/features/auth/utils/authErrorMessage';

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  rememberMe: z.boolean()
});

type FormValues = z.infer<typeof schema>;

export const LoginForm = () => {
  const { t } = useTranslation();
  const { loginMutation } = useAuth();

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: { email: '', password: '', rememberMe: true }
  });

  const handleSubmit = (values: FormValues) => {
    loginMutation.mutate(values);
  };

  return (
    <AuthCard p={{ base: 24, md: 32 }} w={{ base: '100%', md: 420 }}>
      <Stack gap="md">
        <Title order={2} fz={24} fw={700} style={brandTextStyle}>
          {t('auth.formTitle')}
        </Title>

        {(loginMutation.isError || loginMutation.isPaused) && (
          <FormErrorMessage
            message={authErrorMessage(loginMutation.error, loginMutation.isPaused, t, () =>
              t('auth.error')
            )}
          />
        )}

        <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
          <Stack gap="md">
            <TextField label={t('auth.email')} required {...form.getInputProps('email')} />
            <PasswordField
              label={t('auth.password')}
              placeholder={t('auth.passwordPlaceholder')}
              required
              {...form.getInputProps('password')}
            />

            <Group justify="space-between">
              <Checkbox
                size="sm"
                label={t('auth.rememberMe')}
                style={{ color: 'var(--color-text-muted)' }}
                {...form.getInputProps('rememberMe', { type: 'checkbox' })}
              />
              <Anchor
                component={Link}
                to="/forgot-password"
                fz={11}
                fw={600}
                style={{ ...brandTextStyle, letterSpacing: 0.4 }}
              >
                {t('auth.forgotPassword')}
              </Anchor>
            </Group>

            <MutationButton
              type="submit"
              fullWidth
              rightSection={<IconFigmaArrowRight size={15} />}
              isPending={loginMutation.isPending}
              isPaused={loginMutation.isPaused}
            >
              {t('auth.login')}
            </MutationButton>
          </Stack>
        </form>

        <Divider
          label={t('auth.or')}
          labelPosition="center"
          color="var(--color-bg-surface-3)"
          styles={{ label: { color: 'var(--color-text-secondary)', fontSize: 12 } }}
        />

        <SecondaryEntryCard
          icon={IconFigmaBuilding}
          title={t('auth.newOrgTitle')}
          subtitle={t('auth.newOrgSubtitle')}
          to="/register"
        />
      </Stack>
    </AuthCard>
  );
};
